import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Star, CheckCircle, TrendingUp } from 'lucide-react';

const LevelCard = ({ level, onClick, index }) => {
  const { id, title, description, difficulty, reward_xp, is_completed, is_locked } = level;

  const difficultyColors = {
    beginner: 'text-green-400',
    intermediate: 'text-yellow-400',
    advanced: 'text-red-400',
  };

  const difficultyStars = {
    beginner: 1,
    intermediate: 2,
    advanced: 3,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={!is_locked ? { y: -4, scale: 1.02 } : {}}
      className="relative"
    >
      <div
        onClick={!is_locked ? onClick : undefined}
        className={`glass rounded-card p-6 cursor-pointer transition-all duration-300 group ${
          is_locked ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50'
        } ${is_completed ? 'border-success/50' : ''}`}
        data-testid={`level-card-${id}`}
      >
        {/* Locked Overlay */}
        {is_locked && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-card backdrop-blur-sm z-10">
            <Lock className="w-12 h-12 text-text-muted" />
          </div>
        )}

        {/* Completed Badge */}
        {is_completed && (
          <div className="absolute -top-2 -right-2 z-20">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 15 }}
              className="text-3xl"
            >
              ✅
            </motion.div>
          </div>
        )}
        
        {/* Level Number */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-heading font-bold">
              {id}
            </div>
            <div className="flex items-center gap-1">
              {Array.from({ length: difficultyStars[difficulty] }).map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${difficultyColors[difficulty]}`} fill="currentColor" />
              ))}
            </div>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl font-heading font-bold text-text-primary mb-2 group-hover:text-primary transition-colors">
          {title}
        </h3>

        {/* Description */}
        <p className="text-text-secondary text-sm mb-4 line-clamp-2">{description}</p>

        {/* XP Reward */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-primary">
            <TrendingUp className="w-5 h-5" />
            <span className="font-bold">{reward_xp} XP</span>
          </div>
          <div className="text-xs text-text-muted capitalize">{difficulty}</div>
        </div>

        {/* Hover Glow Effect */}
        {!is_locked && (
          <div className="absolute inset-0 rounded-card bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
        )}
      </div>
    </motion.div>
  );
};

export default LevelCard;